<script type="text/jsx">
import omit from 'lodash/omit';
import noop from 'lodash/noop';
import includes from 'lodash/includes';
import PianoLoading from 'library-@/components/PianoLoading';
import TranslationPlaceholder from './TranslationPlaceholder';
import HighlightFormatter from './highlight/HighlightFormatter';
import { LangTypeMode } from '@/domains/translation/enums';

const markIgnore = (value, classes = []) => {
    const klass = ['ignore', ...classes].join(' ');
    return `<mark class="${klass}">${value}</mark>`;
};

export const PT_SECTION__NO_DEFAULT = 'PtSection.NO_DEFAULT';
export const PT_SECTION__NO_ORIGINAL = 'PtSection.NO_ORIGINAL';
export const PT_SECTION__NO_TRANSLATION = 'PtSection.NO_TRANSLATION';
export const PT_SECTION__LANG_MODE_LABEL = 'PtSection.LANG_MODE_LABEL';

const OMIT_ATTRS = [
    'text',
    'label',
    'loading',
    'chunk',
    'lazy-task',
    'lang-mode',
    'lang-mode-label',
    'on-highlight-complete',
    'alternative',
    'failure',
].map(item => `attrs.${item}`);

const lazyOptions = {};

const lazyDirective = {
    directives: [{ name: 'lazy', value: lazyOptions }],
};

export default {
    functional: true,
    render(h, context) {
        const {
            text,
            alternative,
            label = '',
            loading = false,
            chunk,
            lazyTask = noop,
            langMode = LangTypeMode.DEFAULT,
            langModeLabel,
            onHighlightComplete = noop,
            failure,
        } = context.props;
        const alterationMode = langMode !== LangTypeMode.DEFAULT;

        const klass = {
            'pt-section': true,
            'pt-section--alternative': alterationMode,
            'pt-section--original': langMode === LangTypeMode.ORIGINAL,
            'pt-section--translation': langMode === LangTypeMode.TRANSLATION,
            'pt-section--has-alternative': alternative,
            'pt-section--no-label': !label,
        };

        const data = omit(context.data, OMIT_ATTRS);

        if (!data.directives) {
            data.directives = [];
        }

        const existed = data.directives.map(({ name }) => name);

        // 这样使用，要求 context 环境引入了下面 1 个 directives，是否可以考虑在这个组件内部全局注入这 2 个 directives
        ['math'].forEach(name => {
            if (!includes(existed, name)) {
                data.directives.push({ name });
            }
        });

        const translationPlaceholder = alterationMode
            ? <TranslationPlaceholder
                { ...lazyDirective }
                lazyTask={lazyTask}
                chunk={chunk}>
            </TranslationPlaceholder>
            : null;

        if (loading) {
            return (
                <div {...data} class={klass}>
                    {label ? <h3>{label}</h3> : null}
                    {(translationPlaceholder && chunk) ? translationPlaceholder : <PianoLoading class="pt-section__loading" />}
                </div>
            );
        }

        const $t = (...rest) => {
            return context.parent.$t(...rest);
        };

        let content = text || markIgnore($t(PT_SECTION__NO_DEFAULT, [label.toLowerCase()]));

        let placeholder = $t(PT_SECTION__NO_ORIGINAL);
        if (langMode !== LangTypeMode.DEFAULT) {
            if (langMode === LangTypeMode.TRANSLATION) {
                placeholder = $t(PT_SECTION__NO_TRANSLATION);
            }
            content = alternative || markIgnore(placeholder);
        }

        // FIXME: Fix TOC-7352, dirty！
        if (langModeLabel) {
            const modelLabel = markIgnore(langModeLabel, ['strong']);

            if (!/^<p>/.test(content)) {
                content = `<p>${modelLabel}${content}</p>`;
            } else {
                content = content.replace(/^<p>(.*)/, function(m, p) {
                    return `<p>${modelLabel}${p}`;
                });
            }
        }

        // FIXME: 为了同时显示已翻译内容和显示翻译失败，做的硬编码，很不友好
        let failureHit = null;
        if (alterationMode && alternative && failure) {
            failureHit = <p class="pt-section__failure" domPropsInnerHTML={placeholder} />;
        }

        return (
            <div {...data} class={klass}>
                {label ? <h3>{label}</h3> : null}
                <HighlightFormatter
                    rawHtml={content}
                    onCompleted={onHighlightComplete}
                    class="pt-section__body" />
                {translationPlaceholder}
                {failureHit}
            </div>
        );
    },
};
</script>

<style lang="scss">
$grep: #f5f5f5;
@mixin grey-back() {
    padding: 10px;
    background: $grep;
}

.pt-section {
    padding: 10px;
    word-wrap: break-word;
    &__body {
        mark.strong {
            font-weight: 700;
            margin-right: 5px;
        }
    }
    &--alternative {
        .pt-section__body {
            @include grey-back();
        }
    }
    &--no-label {
        padding: 0;
    }
    &--translation {
        /* 在翻译时隐藏 uspto-tbl, uspto-list */
        [class^='uspto-tbl-'],
        .uspto-list {
            display: none;
        }
    }
    &__loading {
        background: $grep;
    }
    &__translation {
        font-weight: bold;
    }
    &__failure {
        @include grey-back();
    }
}
</style>
